import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

/**
 * Input con autocompletado de direcciones vía Google Places (servidor).
 *
 * Props (compatibles con la implementación anterior basada en Mapbox):
 *  - value, onChange (string)
 *  - onSelect ({ direccion, ciudad, provincia, cp, latitud, longitud, place_id, raw })
 *  - placeholder, className, "data-testid"
 *  - regionCode: "es" por defecto (acepta cualquier código ISO-3166-1 alpha-2)
 *  - minChars: 3 por defecto
 *  - debounceMs: 350ms
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Empieza a escribir la dirección…",
  regionCode = "es",
  className = "",
  minChars = 3,
  debounceMs = 350,
  apiClient = null,
  endpoint = "/places/autocomplete",
  "data-testid": dataTestId,
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const timerRef = useRef();
  const wrapRef = useRef();
  const lastQueryRef = useRef("");
  const skipNext = useRef(false);

  useEffect(() => {
    function close(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    clearTimeout(timerRef.current);
    const q = (value || "").trim();
    if (q.length < minChars) {
      setResults([]); setOpen(false); return;
    }
    if (q === lastQueryRef.current) return;
    timerRef.current = setTimeout(async () => {
      lastQueryRef.current = q;
      setLoading(true);
      try {
        const client = apiClient || api;
        const { data } = await client.post(endpoint, {
          query: q,
          region_code: regionCode,
        });
        const items = data?.items || [];
        setResults(items);
        setOpen(items.length > 0);
        setHighlight(items.length > 0 ? 0 : -1);
      } catch (_e) {
        setResults([]);
        setOpen(false);
      } finally { setLoading(false); }
    }, debounceMs);
    return () => clearTimeout(timerRef.current);
  }, [value, regionCode, minChars, debounceMs]);

  const pick = (item) => {
    skipNext.current = true;
    // Si se pasa onSelect → mapear a formato estructurado
    if (onSelect) {
      onSelect({
        direccion: item.address || item.label || "",
        ciudad: item.ciudad || "",
        provincia: item.provincia || "",
        cp: item.cp || "",
        latitud: item.lat ?? null,
        longitud: item.lon ?? null,
        place_id: item.place_id,
        raw: item,
      });
    } else if (onChange) {
      onChange(item.label || item.address || "");
    }
    setOpen(false);
    setResults([]);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={className}
        data-testid={dataTestId}
        autoComplete="off"
      />
      {loading && <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-amber-400" />}
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md shadow-2xl max-h-72 overflow-y-auto"
          data-testid={dataTestId ? `${dataTestId}-suggestions` : "autocomplete-results"}>
          {results.map((s, i) => (
            <button
              key={s.place_id || i}
              type="button"
              onClick={() => pick(s)}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`autocomplete-item-${i}`}
              className={`w-full text-left px-3 py-2 text-xs flex items-start gap-2 border-b border-zinc-900 last:border-b-0 transition-colors ${
                highlight === i ? "bg-amber-500/15 text-amber-100" : "hover:bg-zinc-900 text-zinc-200"
              }`}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
              <div className="min-w-0 flex-1">
                {s.name && s.name !== s.address && (
                  <div className="font-medium truncate">{s.name}</div>
                )}
                <div className={`truncate ${s.name && s.name !== s.address ? "text-[10px] text-zinc-500" : ""}`}>
                  {s.address}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
